import { useState } from 'react';
import { useWebViewRouter } from '@/hooks/useWebViewRouter';
import { receivingType } from '@/types/checkout';
import { useSearchParams } from 'react-router-dom';
import { useCart } from '@/hooks';
import { useFormContext } from 'react-hook-form';
import { initOrderForm } from '@/routes/order';
import { useQueryClient } from '@tanstack/react-query';
import { FormattedMessage, useIntl } from 'react-intl';
import { Image } from '@/components/Image';
import Button from '../Button';
import useToggle from '@/hooks/useToggle';
import AvailableCountriesModal from '@/components/AvailableCountriesModal';
import { useShippingCountries } from '@/hooks/useShippingCountries';

// 배송비 정책
// 픽업 : 뮤료
// 국내 배송 : 5만원 이상 구매시 무료
// 해외 배송 : 10만원 이상 구매시 무료
//모달 분기처리

const ReceivingMethodModal = ({ onClose }: { onClose?: () => void }) => {
  const intl = useIntl();
  const { direct } = useWebViewRouter();
  const [searchParams] = useSearchParams();
  const receivingMethod = searchParams.get('shipping_method') as receivingType;
  const { cartData } = useCart({ receivingMethod });
  const totalPrice = cartData?.offer_total ?? 0;
  const [selected, setSelected] = useState<receivingType | null>(receivingMethod);
  const form = useFormContext();
  const queryClient = useQueryClient();
  const reset = form?.reset ?? (() => {});

  //추석대응
  const today = new Date();

  // 범위 시작과 끝 (연도는 현재 연도로 맞춤)
  const start = new Date(today.getFullYear(), 8, 30); // 8 = 9월
  const end = new Date(today.getFullYear(), 9, 9); // 9 = 10월, 9일

  // 오늘이 9/30 ~ 10/9 사이인지 여부
  const isInPeriod = today >= start && today <= end;
  const shippingMNoticeModal = useToggle({ showing: false });
  const availableCountriesModal = useToggle({ showing: false });
  const { countries: shippingCountries } = useShippingCountries();

  const options = [
    {
      id: 'pick_up',
      title: intl.formatMessage({ id: 'pick_up_at_store', defaultMessage: 'Pick-up at store' }),
      description: intl.formatMessage({
        id: 'available_for_pick_up_at_hongdae_and_myeongdong',
        defaultMessage: 'Available for pick-up at Hongdae and Myeongdong'
      }),
      price: intl.formatMessage({ id: 'free', defaultMessage: 'Free' }),
      disabledPrice: ''
    },
    {
      id: 'domestic',
      title: intl.formatMessage({ id: 'delivery_in_korea', defaultMessage: 'Delivery (In Korea)' }),
      description: intl.formatMessage({
        id: 'it_takes_about_2_3_business_days',
        defaultMessage: ' It takes about 6~8 business days.'
      }),
      price: '₩0',
      disabledPrice: '₩3,000'
    },
    {
      id: 'global',
      title: intl.formatMessage({ id: 'delivery_international', defaultMessage: 'Delivery (International)' }),
      description: intl.formatMessage({
        id: 'it_may_take_up_to_14_days',
        defaultMessage: 'It may take up to 19 days.'
      }),
      price: '₩0',
      disabledPrice: '₩10,000'
    }
  ];

  const handleSelect = (id: receivingType) => {
    setSelected(id);
  };

  const handleClick = () => {
    onClose?.();
    reset({ ...initOrderForm }, { keepErrors: false });
    queryClient.invalidateQueries({ queryKey: ['cart'] });
    direct(`/order?shipping_method=${selected}`);
  };

  return (
    <>
      <div className="fixed inset-0 w-full h-full bg-black/80 flex justify-center items-center z-[1000]" onClick={onClose}>
        <div
          className="min-w-[570px] rounded-2xl bg-white text-center p-10 flex flex-col gap-10 w-[680px]"
          onClick={(e) => e.stopPropagation()}
        >
          <div className="flex flex-col gap-2">
            <h1 className="text-[30px] leading-9 font-extrabold">
              <FormattedMessage id="select_a_receiving_method" defaultMessage="Select a receiving method" />
            </h1>
            <h3 className="text-[20px] leading-6 text-[#4D4D4D] font-medium">
              <FormattedMessage
                id="shipping_fees_may_vary_depending_on_the_delivery_region."
                defaultMessage="Shipping fees may vary depending on the delivery region."
              />
            </h3>
          </div>

          <div className="flex flex-col gap-4">
            {options.map((opt) => {
              const isPickup = opt.id === 'pick_up';
              const isGlobal = opt.id === 'global';
              const isDomestic = opt.id === 'domestic';
              const isInternationalShippingFree = isGlobal && totalPrice >= 100000;

              return (
                <div
                  key={opt.id}
                  className={`rounded-[10px] p-5 border ${selected === opt.id ? 'border-[#ff6060]' : 'border-[#EDEDED]'}`}
                >
                  <div className="flex justify-between items-center w-full" onClick={() => handleSelect(opt.id as receivingType)}>
                    {selected === opt.id ? (
                      <div className="w-6 h-6 rounded flex items-center justify-center mr-4">
                        <Image src="/icons/checkbox_on.svg" alt="checkbox_on-svg" width={24} height={24} />
                      </div>
                    ) : (
                      <div className="w-6 h-6 border border-[#E0E0E0] rounded flex items-center justify-center mr-4" />
                    )}
                    <div className="flex-1 text-left">
                      <p className="text-[16px] font-semibold mb-[2px]">{opt.title}</p>
                      <p className="text-[14px] text-[#4d4d4d]">{opt.description}</p>
                    </div>
                    <div className="text-right flex flex-col items-end">
                      {isInternationalShippingFree || isDomestic || isPickup ? (
                        <>
                          <span className="line-through text-[#999]">{opt.disabledPrice}</span>
                          <span className="font-semibold text-[#f33] text-[16px]">{opt.price}</span>
                        </>
                      ) : (
                        <span className="font-semibold text-[#262626] text-[16px]">{opt.disabledPrice}</span>
                      )}
                    </div>
                  </div>
                  {isGlobal && (
                    <button
                      type="button"
                      className="mt-4 bg-[#fff2f2] p-4 rounded-[10px] w-full flex justify-between items-center cursor-pointer"
                      onClick={(e) => {
                        e.stopPropagation();
                        availableCountriesModal.open();
                      }}
                    >
                      <span className="text-[14px] font-semibold text-[#82181A]">
                        <FormattedMessage
                          id="available_in_n_countries"
                          defaultMessage="Available in {count} countries"
                          values={{ count: shippingCountries.length }}
                        />
                      </span>
                      <span className="flex items-center gap-1 text-[14px] font-semibold text-[#82181A]">
                        <FormattedMessage id="view_list" defaultMessage="View List" />
                        <Image src="/icons/arrow-right-red.svg" alt="arrow-right-red-svg" width={16} height={16} />
                      </span>
                    </button>
                  )}
                </div>
              );
            })}
          </div>

          <button
            className="w-full h-[60px] rounded-[20px] bg-[#ff5b5b] text-white font-bold text-[18px] disabled:bg-[#ffcccc]"
            disabled={!selected}
            onClick={() => {
              selected === 'pick_up' || !isInPeriod ? handleClick() : shippingMNoticeModal.open();
            }}
          >
            <FormattedMessage id="continue" defaultMessage="Continue" />
          </button>
        </div>
      </div>

      {availableCountriesModal.isShowing && <AvailableCountriesModal onClose={availableCountriesModal.close} />}

      {shippingMNoticeModal.isShowing && (
        <div className="fixed inset-0 w-full h-full bg-black/80 flex justify-center items-center z-[1000]" onClick={onClose}>
          <div
            className="rounded-2xl bg-white text-center p-10 flex flex-col gap-2 w-[568px]"
            onClick={(e) => e.stopPropagation()}
          >
            <p className="text-[30px] text-[#262626] font-extrabold">
              <FormattedMessage id="shipping-notice-title" defaultMessage="Shipping Notice" />
            </p>
            <p className="text-[20px] text-[#4D4D4D] font-medium pb-10">
              <FormattedMessage id="shipping-notice-desc1" defaultMessage="Due to Chuseok," /> <br />
              <FormattedMessage id="shipping-notice-desc2" defaultMessage="orders placed from Sep 30 (Tue) to Oct 9 (Thu)" />
              <br />
              <FormattedMessage id="shipping-notice-desc3" defaultMessage="will be shipped starting Oct 10 (Fri)." />
            </p>

            <div className="flex flex-col gap-3 w-full">
              <Button className="w-full" data-primary onClick={handleClick}>
                <FormattedMessage id="shipping-confirm" defaultMessage="Confirm" />
              </Button>

              <Button className="bg-[#f5f5f5] w-full" textColor={'#000'} onClick={shippingMNoticeModal.close}>
                <FormattedMessage id="change-receiving-method" defaultMessage="Change receiving method" />
              </Button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ReceivingMethodModal;
